<!-- src/components/Register.vue -->
<template>
  <div class="full-screen-background">
    <v-container class="pa-4 fill-height">
      <v-row align="center" justify="center">
        <!-- Coluna do formulário -->
        <v-col cols="12" sm="8" md="4">
          <div class="text-center">
            <h1 class="mb-4">{{ t('registro.titleRegister') }}</h1>
          </div>

          <!-- ALERTAS -->
          <v-alert
            v-if="alert.message"
            :type="alert.type"
            dense
            dismissible
            class="mb-4"
            @input="alert.message = ''"
          >
            {{ alert.message }}
          </v-alert>

          <v-form @submit.prevent="onSubmit">
            <!-- Usuário -->
            <v-text-field
              v-model="mid"
              :label="t('registro.labelUsuario')"
              :rules="[v => !!v || t('registro.ruleUsuarioRequired')]"
              required
            />

            <!-- Apelido -->
            <v-text-field
              v-model="mid_surname"
              :label="t('registro.labelApelido')"
              :rules="[v => !!v || t('registro.ruleApelidoRequired')]"
              required
            />

            <!-- E-mail -->
            <v-text-field
              v-model="email"
              :label="t('registro.labelEmail')"
              type="email"
              :rules="[
                v => !!v || t('registro.ruleEmailRequired'),
                v => emailValid || t('registro.ruleEmailInvalid')
              ]"
              required
            />

            <!-- Senha -->
            <v-text-field
              v-model="password"
              :type="showPassword ? 'text' : 'password'"
              :append-inner-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'"
              @click:append-inner="showPassword = !showPassword"
              :label="t('registro.labelSenha')"
              :rules="[
                v => !!v || t('registro.ruleSenhaRequired'),
                v => v.length >= 8 || t('registro.ruleSenhaMin'),
                v => /[A-Za-z]/.test(v) || t('registro.ruleSenhaLetter'),
                v => /\d/.test(v) || t('registro.ruleSenhaNumber'),
                v => /[^A-Za-z0-9]/.test(v) || t('registro.ruleSenhaSymbol')
              ]"
              required
            />
            <v-progress-linear
              :value="strengthPercent"
              :color="strengthColor"
              height="7"
              class="mb-1"
            />
            <div class="caption mb-4">
              {{ t('registro.strengthLabelPrefix') }}: {{ strengthLabel }}
            </div>

            <!-- Repetir Senha -->
            <v-text-field
              v-model="repeatPassword"
              :type="showRepeat ? 'text' : 'password'"
              :append-inner-icon="showRepeat ? 'mdi-eye-off' : 'mdi-eye'"
              @click:append-inner="showRepeat = !showRepeat"
              :label="t('registro.labelRepeatPassword')"
              :rules="[
                v => !!v || t('registro.ruleConfirmationRequired'),
                v => v === password || t('registro.rulePasswordsMatch')
              ]"
              required
            />

            <!-- Botão centralizado -->
            <div class="text-center">
              <v-btn
                type="submit"
                class="mt-4"
                color="red"
                :disabled="!isFormValid"
              >
                {{ t('registro.btnRegister') }}
              </v-btn>
            </div>
          </v-form>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import { register } from '../services/api'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()
const router = useRouter()

const mid = ref('')
const mid_surname = ref('')
const email = ref('')
const password = ref('')
const repeatPassword = ref('')
const showPassword = ref(false)
const showRepeat = ref(false)

// Para controlar os alertas
const alert = ref({ type: '', message: '' })

const emailValid = computed(() =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.value)
)

const strengthScore = computed(() => {
  let s = 0
  if (password.value.length >= 8)         s++
  if (/[A-Za-z]/.test(password.value))     s++
  if (/\d/.test(password.value))           s++
  if (/[^A-Za-z0-9]/.test(password.value)) s++
  if (password.value.length >= 12)        s++
  return s
})

const strengthPercent = computed(() =>
  Math.min(100, (strengthScore.value / 5) * 100)
)

const strengthColor = computed(() => {
  const p = strengthPercent.value
  if (p < 40)    return 'error'
  if (p < 80)    return 'warning'
  return 'success'
})

const strengthLabel = computed(() => {
  const p = strengthPercent.value
  if (p < 40)    return t('registro.strengthFraca')
  if (p < 80)    return t('registro.strengthMedia')
  return t('registro.strengthForte')
})

const isFormValid = computed(() =>
  mid.value &&
  mid_surname.value &&
  emailValid.value &&
  strengthScore.value >= 4 &&
  password.value === repeatPassword.value
)

async function onSubmit() {
  alert.value.message = ''

  try {
    const data = await register(
      mid.value,
      mid_surname.value,
      email.value,
      password.value
    )
    // Exibe alerta de sucesso
    alert.value = {
      type: 'success',
      message: data.message || t('registro.alertSuccess')
    }
    // Redireciona para confirmação
    router.push({
      name: 'ConfirmRegistration',
      query: { email: email.value }
    })
  } catch (err) {
    // Exibe erro no alert
    alert.value = { type: 'error', message: err.message }
  }
}
</script>

<style scoped>
.caption {
  text-align: right;
  font-weight: 500;
}
.login-img {
  margin-left: -30%;
  display: block;
  width: 150%;
  max-width: 800px;
  height: auto;
}
.full-screen-background {
  background-image: url('@/assets/background/cassino/cassinoz.png');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  min-height: 100vh;
  width: 100%;
  position: relative;
}
</style>
